<template>
    <div class="flex flex-col gap-y-5 text-sm">
        <!-- At a glance: how many runs are still open (running or waiting)
             and how many are done — failed ones apart. -->
        <div
            v-if="store.tasks.length > 0"
            class="flex flex-wrap items-center gap-2"
            data-testid="vectorize-queue-summary"
        >
            <span
                class="bg-primary-10 text-primary-6 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium tabular-nums"
                data-testid="vectorize-queue-summary-open"
            >
                <SmuIcon icon="hourglass-half" type="light" />
                {{
                    t("vectorize.queue.summary_open", {
                        count: store.pendingCount,
                    })
                }}
            </span>
            <span
                class="inline-flex items-center gap-1.5 rounded-full bg-green-50 px-2.5 py-1 text-xs font-medium text-green-700 tabular-nums"
                data-testid="vectorize-queue-summary-done"
            >
                <SmuIcon icon="circle-check" type="light" />
                {{ t("vectorize.queue.summary_done", { count: doneCount }) }}
            </span>
            <span
                v-if="failedCount > 0"
                class="inline-flex items-center gap-1.5 rounded-full bg-red-50 px-2.5 py-1 text-xs font-medium text-red-600 tabular-nums"
                data-testid="vectorize-queue-summary-failed"
            >
                <SmuIcon icon="triangle-exclamation" type="light" />
                {{
                    t("vectorize.queue.summary_failed", { count: failedCount })
                }}
            </span>
        </div>

        <p class="text-neutral-5 text-xs">
            {{ t("vectorize.queue.background_hint") }}
        </p>

        <p
            v-if="store.tasks.length === 0"
            class="text-neutral-5 rounded-lg border border-dashed px-3 py-6 text-center"
        >
            {{ t("vectorize.queue.empty") }}
        </p>

        <!-- Läuft gerade: höchstens eine Aufgabe -->
        <section v-if="store.active" class="flex flex-col gap-y-2">
            <h3 class="text-neutral-4 text-xs font-semibold uppercase">
                {{ t("vectorize.queue.running") }}
            </h3>
            <div
                class="border-primary-6 bg-primary-10/40 flex flex-col gap-y-2 rounded-lg border p-2"
                data-testid="vectorize-queue-active"
            >
                <div class="flex items-center gap-3">
                    <VectorizeQueueThumbnail :task="store.active" />
                    <div class="min-w-0 flex-1">
                        <p class="truncate font-medium">
                            {{ store.taskLabel(store.active) }}
                        </p>
                        <p class="text-neutral-5 truncate text-xs">
                            {{ progressLabel(store.active) }}
                        </p>
                    </div>
                    <span
                        class="text-neutral-5 shrink-0 font-mono text-xs tabular-nums"
                    >
                        {{ elapsed(store.active) }}
                    </span>
                </div>
                <div
                    class="bg-neutral-10 h-1 w-full overflow-hidden rounded-full"
                >
                    <div
                        class="bg-primary-6 h-full rounded-full transition-[width] duration-700"
                        :class="{
                            'animate-pulse':
                                progressPercent(store.active) === 0,
                        }"
                        :style="{
                            width: `${String(Math.max(4, progressPercent(store.active)))}%`,
                        }"
                    />
                </div>
                <div class="flex flex-wrap justify-end gap-2">
                    <VectorizeQueueCancelButton :task-id="store.active.id" />
                    <SmuButton
                        size="sm"
                        data-testid="vectorize-queue-watch"
                        @click="store.openLive(store.active.id)"
                    >
                        <SmuIcon icon="eye" type="light" class="mr-1" />
                        {{ t("vectorize.queue.watch_live") }}
                    </SmuButton>
                </div>
            </div>
        </section>

        <!-- Warteschlange: ziehen oder mit den Pfeilen umsortieren -->
        <section v-if="store.queued.length > 0" class="flex flex-col gap-y-2">
            <h3 class="text-neutral-4 text-xs font-semibold uppercase">
                {{
                    t("vectorize.queue.waiting", {
                        count: store.queued.length,
                    })
                }}
            </h3>
            <ol class="flex flex-col gap-y-1" data-testid="vectorize-queue">
                <li
                    v-for="(task, index) in store.queued"
                    :key="task.id"
                    draggable="true"
                    class="group flex items-center gap-2 rounded-lg border p-2 transition-colors"
                    :class="{
                        'opacity-40': draggedId === task.id,
                        'border-primary-6 border-t-2': dropIndex === index,
                    }"
                    data-testid="vectorize-queue-item"
                    @dragstart="onDragStart($event, task.id)"
                    @dragover.prevent="onDragOver(index)"
                    @dragleave="onDragLeave(index)"
                    @drop.prevent="onDrop(index)"
                    @dragend="onDragEnd"
                >
                    <SmuIcon
                        icon="grip-dots-vertical"
                        type="light"
                        class="text-neutral-5 shrink-0 cursor-grab"
                        :title="t('vectorize.queue.drag_hint')"
                    />
                    <span
                        class="text-neutral-5 w-4 shrink-0 text-center text-xs tabular-nums"
                    >
                        {{ index + 1 }}
                    </span>
                    <VectorizeQueueThumbnail :task="task" />
                    <button
                        type="button"
                        class="min-w-0 flex-1 cursor-pointer text-left"
                        @click="store.openLive(task.id)"
                    >
                        <p class="truncate font-medium">
                            {{ store.taskLabel(task) }}
                        </p>
                        <p class="text-neutral-5 truncate text-xs">
                            {{ taskSummary(task) }}
                        </p>
                    </button>
                    <div class="flex shrink-0 items-center">
                        <button
                            type="button"
                            class="hover:bg-neutral-10 rounded p-1 disabled:opacity-30"
                            :disabled="index === 0"
                            :title="t('vectorize.queue.move_up')"
                            :aria-label="t('vectorize.queue.move_up')"
                            data-testid="vectorize-queue-up"
                            @click="store.move(task.id, -1)"
                        >
                            <SmuIcon icon="arrow-up" type="light" />
                        </button>
                        <button
                            type="button"
                            class="hover:bg-neutral-10 rounded p-1 disabled:opacity-30"
                            :disabled="index === store.queued.length - 1"
                            :title="t('vectorize.queue.move_down')"
                            :aria-label="t('vectorize.queue.move_down')"
                            data-testid="vectorize-queue-down"
                            @click="store.move(task.id, 1)"
                        >
                            <SmuIcon icon="arrow-down" type="light" />
                        </button>
                        <button
                            type="button"
                            class="rounded p-1 text-red-600 hover:bg-red-50"
                            :title="t('vectorize.queue.remove')"
                            :aria-label="t('vectorize.queue.remove')"
                            data-testid="vectorize-queue-remove"
                            @click="store.remove(task.id)"
                        >
                            <SmuIcon icon="trash" type="light" />
                        </button>
                    </div>
                </li>
            </ol>
        </section>

        <!-- Fertig: Ergebnis ansehen, Fehlgeschlagenes und Abgebrochenes
             erneut einreihen -->
        <section v-if="store.finished.length > 0" class="flex flex-col gap-y-2">
            <h3 class="text-neutral-4 text-xs font-semibold uppercase">
                {{ t("vectorize.queue.finished") }} ({{
                    store.finished.length
                }})
            </h3>
            <ul class="flex flex-col gap-y-1">
                <li
                    v-for="task in store.finished"
                    :key="task.id"
                    class="hover:bg-neutral-10/60 flex items-center gap-2 rounded-lg p-2"
                    data-testid="vectorize-queue-finished"
                >
                    <SmuIcon
                        :icon="finishedIcon(task).icon"
                        type="light"
                        class="shrink-0"
                        :class="finishedIcon(task).class"
                    />
                    <VectorizeQueueThumbnail :task="task" />
                    <button
                        type="button"
                        class="min-w-0 flex-1 cursor-pointer text-left"
                        @click="store.openLive(task.id)"
                    >
                        <p class="truncate">{{ store.taskLabel(task) }}</p>
                        <p
                            class="truncate text-xs"
                            :class="
                                task.status === 'failed'
                                    ? 'text-red-600'
                                    : 'text-neutral-5'
                            "
                        >
                            {{ finishedLabel(task) }}
                        </p>
                    </button>
                    <div class="flex shrink-0 items-center">
                        <button
                            v-if="
                                task.status === 'failed' ||
                                task.status === 'cancelled'
                            "
                            type="button"
                            class="hover:bg-neutral-10 rounded p-1"
                            :title="t('helpers.buttons.vectorize_retry')"
                            :aria-label="t('helpers.buttons.vectorize_retry')"
                            @click="store.retry(task.id)"
                        >
                            <SmuIcon icon="rotate-right" type="light" />
                        </button>
                        <button
                            type="button"
                            class="hover:bg-neutral-10 rounded p-1"
                            :title="t('vectorize.queue.dismiss')"
                            :aria-label="t('vectorize.queue.dismiss')"
                            @click="store.remove(task.id)"
                        >
                            <SmuIcon icon="xmark" type="light" />
                        </button>
                    </div>
                </li>
            </ul>
        </section>
    </div>
</template>

<script setup lang="ts">
import { SmuButton, SmuIcon, type SmuIconName } from "@smakecloud/smake-ui";
import { useNow } from "@vueuse/core";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import type { VectorizeTask } from "~/lib/api/types/vectorizeTasks";

const { t } = useI18n();
const store = useVectorizeTaskStore();
const now = useNow({ interval: 1000 });

const draggedId = ref<string>();
const dropIndex = ref<number>();

function elapsed(task: VectorizeTask): string {
    if (!task.startedAt) return "";

    const totalSeconds = Math.max(
        0,
        Math.floor((now.value.getTime() - task.startedAt) / 1000),
    );

    return `${String(Math.floor(totalSeconds / 60)).padStart(2, "0")}:${String(totalSeconds % 60).padStart(2, "0")}`;
}

/** Runs that finished with a result. */
const doneCount = computed(
    () => store.finished.filter((task) => task.status === "done").length,
);
/** Runs that failed — not those cancelled on purpose. */
const failedCount = computed(
    () => store.finished.filter((task) => task.status === "failed").length,
);

function finishedIcon(task: VectorizeTask): {
    class: string;
    icon: SmuIconName;
} {
    if (task.status === "done") {
        return { class: "text-green-600", icon: "circle-check" };
    }

    if (task.status === "cancelled") {
        return { class: "text-neutral-5", icon: "circle-stop" };
    }

    return { class: "text-red-600", icon: "triangle-exclamation" };
}

function finishedLabel(task: VectorizeTask): string {
    if (task.status === "failed") {
        return task.error ?? t("vectorize.queue.failed");
    }

    if (task.status === "cancelled") return t("vectorize.queue.cancelled");

    const seconds =
        task.startedAt && task.finishedAt
            ? Math.round((task.finishedAt - task.startedAt) / 1000)
            : undefined;
    const time = task.finishedAt
        ? new Date(task.finishedAt).toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
          })
        : "";

    return seconds === undefined
        ? time
        : t("vectorize.queue.finished_at", { seconds, time });
}

function onDragEnd() {
    draggedId.value = undefined;
    dropIndex.value = undefined;
}

function onDragLeave(index: number) {
    if (dropIndex.value === index) dropIndex.value = undefined;
}

function onDragOver(index: number) {
    if (draggedId.value) dropIndex.value = index;
}

function onDragStart(event: DragEvent, id: string) {
    draggedId.value = id;

    if (event.dataTransfer) {
        event.dataTransfer.effectAllowed = "move";
        event.dataTransfer.setData("text/plain", id);
    }
}

function onDrop(index: number) {
    const id = draggedId.value;

    onDragEnd();

    if (!id) return;

    const ids = store.queued.map((task) => task.id);
    const from = ids.indexOf(id);

    if (from === -1 || from === index) return;

    ids.splice(from, 1);
    ids.splice(index, 0, id);
    void store.reorder(ids);
}

function progressLabel(task: VectorizeTask): string {
    const progress = task.progress;

    if (!progress) return t("vectorize.queue.starting");

    if (progress.phase === "upscaling") {
        return t("helpers.inputs.label.vectorize_phase_upscale");
    }

    const parts = [
        task.kind === "svg-edit"
            ? t("helpers.inputs.label.vectorize_phase_edit")
            : t("helpers.inputs.label.vectorize_phase_vectorize"),
    ];

    if (progress.step > 0) {
        parts.push(
            progress.maxIterations > 0
                ? t("helpers.inputs.label.vectorize_step_of", {
                      max: progress.maxIterations,
                      step: progress.step,
                  })
                : t("helpers.inputs.label.vectorize_step", {
                      step: progress.step,
                  }),
        );
    }

    if (progress.tool) parts.push(toolLabel(progress.tool));

    return parts.join(" · ");
}

/** Steps against the step cap — the agent usually stops well before it. */
function progressPercent(task: VectorizeTask): number {
    const progress = task.progress;

    if (!progress || progress.maxIterations === 0) return 0;

    return Math.min(95, (progress.step / progress.maxIterations) * 100);
}

function taskSummary(task: VectorizeTask): string {
    const instruction = task.params.instruction?.trim();
    const kind =
        task.kind === "svg-edit"
            ? t("vectorize.queue.kind_edit")
            : t("vectorize.queue.kind_vectorize");

    return instruction ? `${kind}: ${instruction}` : kind;
}

function toolLabel(tool: string): string {
    const key = `helpers.inputs.label.vectorize_tool_${tool}`;
    const value = t(key);

    return value === key ? tool : value;
}
</script>
